import { useState, useEffect } from 'react';
import { useNavigation } from '../../contexts/NavigationContext';
import { useSession } from '../../contexts/SessionContext';
import { useConnectionContext } from '../../contexts/ConnectionStateContext';
import { getConnections } from '../../services/storage/connectionStorage';
import { getAnonData, getNicknamesForConnections } from '../../services/storage/userDataStorage';
import { getAnonUserId, getSenderId } from '../../utils/identityUtils';
import { loadMessageHistory } from '../../services/chat/messageStore';
import { formatLocationPillPrimary } from '../../utils/locationFormatters';
import { formatRelativeTime } from '../../utils/dateHelpers';
import { getSharedHometown } from '../../utils/connectionName';
import { isFullSender } from '../../utils/contactType';
import { latestSharedImageUrl } from '../../utils/chatHelpers';
import AnonPreviewCard from '../UI/Cards/AnonPreviewCard';
import AnonConnectionsHeader from '../UISystem/Headers/AnonConnectionsHeader';
import LaunchSignupOverlay from '../Overlay/LaunchSignupOverlay';
import { ButtonRounded } from '../UISystem/Buttons';
import PeopleQrPitch from './variants/thread/PeopleQrPitch';
import { COLORS } from '../../styles/UISystem';
import { hasDownstreamLadder, isSansChatVariant } from '../../utils/exchangeVariant';
import { isSimpleTier } from '../../utils/userTier';
import { Lightning } from '@phosphor-icons/react/dist/ssr/Lightning';
import { useSimpleProfileMint } from '../../hooks/useSimpleProfileMint';
import { hasJoinedWaitlist } from '../../services/waitlist';
import { CROWN_WHITE_SMALL } from '../../styles/brandAssets';
import { logFunnelSignupWaitlist } from '../../services/analytics/eventLogger';
import NoticeCard from '../UISystem/NoticeCard/NoticeCard';

// localStorage key for sort preference (shared with ProfileConnectionsListPage)
const SORT_MODE_KEY = 'connections_sort_mode';

/**
 * AnonConnectionsListPage Component
 *
 * The Anon "People" page — the value preview. Shows the connection(s) as a large,
 * inviting card (hometown when the Profile shared it) with quiet identity framing
 * below. No tab bar (dropped for Anon).
 *
 * THE MAIN PATH IS THE APP (user, 2026-08-20). The page used to argue for the
 * QR — a three-line pitch card above the Connect button — on the theory that an
 * account-less person needed persuading that the QR was for them. The reversal:
 * "the app should speak for itself, and people should know that it's an app."
 * So the pitch collapses to one line, and the docked Get-Royale banner returns
 * below the button as the primary onward move.
 *
 * The Connect/QR flow stays, unchanged and untouched, as the secondary path —
 * it is still the only way to produce funnel step 3, and whether an
 * account-less person understands they can use it at all is exactly the open
 * question. Two doors, in a deliberate order: app first, QR still there.
 *
 * Live neighborhood is intentionally NOT shown here — a cold Anon can't decode an
 * unlabeled green pin as "now," and it has no home on Detail/Chat. It returns,
 * labeled and consistent, with the Nearby view (project_nearby_view_design).
 */
const AnonConnectionsListPage = () => {
  // One read per render — the variant can't change mid-load (resolve-once).
  // The QR-ladder framing of People: no chat icons, photo receipts, and the
  // "Make custom QR" door. CARD/TEXT have always taken it; THREAD joins them
  // per anon_8 (plan 3 D3/D15) — it is the only People-page shape that offers
  // the mint door, which THREAD needs to produce step-3 data.
  const ladderTreatment = hasDownstreamLadder();
  // ...but the QR PITCH is only for someone who doesn't have one yet. This page
  // has no tier gate of its own, and the thread's count badge routes here, so a
  // minted Simple profile coming back through a thread was being sold a QR they
  // already own (found 2026-08-16 while answering "does the message go away
  // after they create a QR?" — it does for the normal path, because minting
  // moves them to the Profile People page for good, but not for that one).
  // Hoisted: isSimpleTier() walks the WHOLE localStorage keyspace and parses
  // the auth-token blob on every call, and it is no more able to change
  // mid-page than the variant above it.
  const [minted] = useState(() => isSimpleTier());
  const showQrPitch = ladderTreatment && !minted;
  const { navigateTo, screenParams, showQR } = useNavigation();

  // Get session context
  const { sessionChatRoomId, updateSessionChatRoomId } = useSession();

  // Get user preferences from context
  const { setCurrentChatRoomId } = useConnectionContext();

  // State for connection data
  const [connections, setConnections] = useState([]);
  // The owner's descriptors ("About Mark" field), DB-first — painted async
  const [nicknames, setNicknames] = useState({});
  const [isLoading, setIsLoading] = useState(true);
  // Prefills the LaunchSignupOverlay behind the Get-Royale banner. (The dead
  // PendingProfileOverlay mount that shared them went in E3a — _refactor :90.)
  const [anonPrefillPhone, setAnonPrefillPhone] = useState('');
  const [anonPrefillName, setAnonPrefillName] = useState('');
  // The Get-Royale banner's overlay (see the docblock: the app is the main path
  // on this page as of 2026-08-20).
  const [showGetApp, setShowGetApp] = useState(false);
  // Read ONCE at mount, not per render — same rule as ProfileConnectionsListPage's
  // devToolsOn, which was written in this same commit and then broken here. The
  // banner retires on join, so the completion handler sets it directly rather
  // than the render body re-reading localStorage on every list re-render.
  const [joinedWaitlist, setJoinedWaitlist] = useState(hasJoinedWaitlist);
  // Read sort mode from localStorage (set by Profile user) but anon can't change it
  const [sortMode] = useState(() => {
    return localStorage.getItem(SORT_MODE_KEY) || 'met';
  });

  // Reset scroll position on mount
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // Sync currentChatRoomId in ConnectionStateContext with sessionChatRoomId
  // This ensures userPreferences.hasSignedUpForQR loads for the correct session
  useEffect(() => {
    if (sessionChatRoomId) {
      setCurrentChatRoomId(sessionChatRoomId);
    }
  }, [sessionChatRoomId, setCurrentChatRoomId]);

  // Load the Anon's phone for the PWA-conversion overlay prefill
  useEffect(() => {
    if (sessionChatRoomId) {
      const loadAnonData = async () => {
        try {
          const anonData = await getAnonData(sessionChatRoomId);
          if (anonData?.phone) setAnonPrefillPhone(anonData.phone);
          if (anonData?.name) setAnonPrefillName(anonData.name);
        } catch (error) {
          console.error('[ANON_CONNECTIONS_LIST] Error loading Anon data for prefill:', error);
        }
      };
      loadAnonData();
    }
  }, [sessionChatRoomId]);

  // Get the latest message timestamp from a connection's chat history (for sort)
  const getLatestMessageTimestamp = (chatMessages) => {
    if (!chatMessages || chatMessages.length === 0) return null;
    const userMessages = chatMessages.filter(
      (msg) => msg.type === 'chat' || msg.type === 'contact_share' || msg.type === 'contact'
    );
    if (userMessages.length === 0) return null;
    const latestMessage = userMessages[userMessages.length - 1];
    return latestMessage?.timestamp || null;
  };

  // Load connection data for anon user (extracted for reuse)
  const loadConnections = async () => {
    try {
      const allConnections = await getConnections();

      if (!allConnections || !allConnections.length) {
        setIsLoading(false);
        return;
      }

      // Show all exchange connections — even before Profile shares name
      const validConnections = allConnections.map((conn) => ({
        chatRoomId: conn.chatRoomId,
        profileData: conn.profileData || {},
        connectionMetadata: conn.connectionMetadata || {},
        chatMessages: conn.chatMessages || [],
        notes: { text: conn.notes || '' },
      }));

      // Sort based on current sort mode
      validConnections.sort((a, b) => {
        if (sortMode === 'activity') {
          const aActivity =
            getLatestMessageTimestamp(a.chatMessages) ||
            a.connectionMetadata?.timestamp ||
            a.connectionMetadata?.meetingDate ||
            '';
          const bActivity =
            getLatestMessageTimestamp(b.chatMessages) ||
            b.connectionMetadata?.timestamp ||
            b.connectionMetadata?.meetingDate ||
            '';
          return new Date(bActivity) - new Date(aActivity);
        } else {
          const aTime =
            a.connectionMetadata?.timestamp ||
            a.connectionMetadata?.meetingDate ||
            a.connectionMetadata?.lastUpdated ||
            '';
          const bTime =
            b.connectionMetadata?.timestamp ||
            b.connectionMetadata?.meetingDate ||
            b.connectionMetadata?.lastUpdated ||
            '';
          return new Date(bTime) - new Date(aTime);
        }
      });

      setConnections(validConnections);
      setIsLoading(false);

      // Nicknames — batched DB-first read, painted when it lands (parity with
      // the Profile card, decision 2026-07-19)
      const userId = getSenderId();
      if (userId) {
        getNicknamesForConnections(
          validConnections.map((c) => c.chatRoomId),
          userId
        )
          .then(setNicknames)
          .catch((err) => console.error('[ANON_CONNECTIONS_LIST] Nickname lookup failed:', err));
      }
    } catch (error) {
      console.error('[ANON_CONNECTIONS_LIST] Error loading connections:', error);
      setIsLoading(false);
    }
  };

  // Sync from DB and load connections on mount and when screen params change
  useEffect(() => {
    const syncAndLoad = async () => {
      try {
        const allConnections = await getConnections();
        await Promise.all(allConnections.map((conn) => loadMessageHistory(conn.chatRoomId)));
      } catch (error) {
        console.error('[ANON_CONNECTIONS_LIST] Error syncing from DB on mount:', error);
      }
      loadConnections();
    };
    syncAndLoad();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [screenParams, sessionChatRoomId, sortMode]);

  // Sync messages from DB when app returns to foreground
  useEffect(() => {
    const handleVisibilityChange = async () => {
      if (document.visibilityState === 'visible') {
        try {
          const allConnections = await getConnections();
          await Promise.all(allConnections.map((conn) => loadMessageHistory(conn.chatRoomId)));
        } catch (error) {
          console.error('[ANON_CONNECTIONS_LIST] Error syncing from DB:', error);
        }
        loadConnections();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Profile (card tap) → detail page
  const handleProfileClick = (chatRoomId) => {
    updateSessionChatRoomId(chatRoomId);
    navigateTo('anon-connection-detail', { source: 'connection-list' });
  };

  // Chat link → chat page (legacy flow only — the icon is hidden under
  // sans-chat and this route stays dormant, D1)
  const handleMessageClick = (chatRoomId) => {
    // Route by whether a THREAD EXISTS, not by the ladder. CARD and TEXT have
    // no chat, so their card sends you to Detail; CHAT and THREAD open the
    // room. Gating this on the ladder sent THREAD to Detail — the arrow fired
    // correctly and simply arrived in the wrong place, which is the same
    // conflation that hid the icon in the first place.
    if (isSansChatVariant()) {
      handleProfileClick(chatRoomId);
      return;
    }
    updateSessionChatRoomId(chatRoomId);
    navigateTo('anon-connection-page', { skipInitialOverlay: true, source: 'connection-list' });
  };

  // "⚡ Get your QR" — the single People_A CTA (flow simplification, 2026-08-07).
  // Mints the Simple profile from the share-back record (same anonymous auth,
  // no OTP), then lands on the Profile People page (D5) with the instant QR
  // open. Only reachable post-share-back, so a name is always on record.
  // Retry / slow / failed handling lives in the shared mint hook (E3a Step 2).
  const {
    minting,
    mintFailed,
    mint: handleGetYourQR,
    retryMint,
  } = useSimpleProfileMint({
    sessionChatRoomId,
    // Seed room: the session room when it holds the share-back name, else
    // the first connection whose record does (a just-saved room has none).
    rooms: connections.map((c) => c.chatRoomId),
    onMinted: () => {
      // Straight to the QR — nothing is interposed between asking for it and
      // getting it (user ruling 2026-08-16). The photo pitch is raised on the
      // QR screen itself, over a QR that already works.
      navigateTo('profile-connections-list', {}, true);
      showQR();
    },
  });

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        height: '100dvh',
        overflow: 'hidden',
        width: '100%',
        backgroundColor: COLORS.background,
      }}
    >
      {/* Header with "People" title */}
      <AnonConnectionsHeader />

      {/* Content area */}
      <div
        style={{
          flex: 1,
          overflowY: 'auto',
          overflowX: 'hidden',
          paddingTop: '72px', // clear the fixed header (matches the Profile search position)
          // Clears the pinned Connect group at the bottom. The group grew when
          // the Get-Royale banner joined it (2026-08-20): invitation line + gap
          // + button + gap + banner + the 24px bottom offset and the safe area.
          // 150px cleared the old two-child group with nothing spare, so the
          // last card sat under the banner on any list long enough to scroll.
          paddingBottom: '250px',
          WebkitOverflowScrolling: 'touch',
        }}
      >
        {isLoading ? (
          <div style={{ padding: '20px', textAlign: 'center' }}>Loading...</div>
        ) : connections.length === 0 ? (
          <div style={{ padding: '20px', textAlign: 'center', color: '#8E8E93' }}>
            No connections yet
          </div>
        ) : (
          <div
            style={{
              padding: '0 16px 20px 16px',
              width: '100%',
              maxWidth: '600px',
              margin: '0 auto',
              boxSizing: 'border-box',
              minHeight: '100%',
              display: 'flex',
              flexDirection: 'column',
            }}
          >
            {/* Connection cards (Anon has 1-2; no search, no list chrome) */}
            <div>
              {connections.map((connection, index) => (
                <div
                  key={connection.chatRoomId}
                  style={{ marginBottom: index < connections.length - 1 ? '16px' : '0' }}
                >
                  <AnonPreviewCard
                    name={connection.profileData.name || 'New Connection'}
                    nickname={nicknames[connection.chatRoomId] || null}
                    photoUrl={connection.profileData.photoUrl}
                    hometown={getSharedHometown(connection.profileData)}
                    venue={formatLocationPillPrimary(connection.connectionMetadata)}
                    metTime={formatRelativeTime(
                      connection.connectionMetadata?.meetingDate ||
                        connection.connectionMetadata?.timestamp
                    )}
                    onCardClick={() => handleProfileClick(connection.chatRoomId)}
                    onChatClick={() => handleMessageClick(connection.chatRoomId)}
                    // The chat door asks a DIFFERENT question than the ladder:
                    // "is there a thread to go back to?" CARD and TEXT have
                    // none, THREAD and CHAT do. Gating it on the ladder
                    // predicate conflated the two and hid the door on the one
                    // variant whose whole premise is the thread.
                    showChatIcon={!isSansChatVariant()}
                    // Ring = FULL hosts only (isFullSender: missing marker = legacy = Full)
                    isRoyaleUser={isFullSender(connection.profileData)}
                    // Latest shared photo — the receipt on the card, not just
                    // Detail (device pass 2; history is already synced above)
                    photoThumbUrl={
                      ladderTreatment ? latestSharedImageUrl(connection.chatMessages) : null
                    }
                  />
                </div>
              ))}
            </div>

            {/* Identity framing, centered below the card. The "Get Royale beta" button
                that lived here was removed (2026-07-17): a real tester read the big
                unprompted CTA as a demand and froze ("what am I supposed to do?").
                Conversion asks are pull-based now — the "Join Royale to keep this chat"
                line in chat, the expired screen, and Profile-sent invites. The signup
                overlay that the button opened was removed with it (its only other
                trigger, the tab-bar QR press, can't fire — the tab bar renders on
                Profile screens only). */}
            {!ladderTreatment && (
              <div
                style={{
                  flex: 1,
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'center',
                  textAlign: 'center',
                }}
              >
                <div
                  style={{
                    fontSize: '17px',
                    fontWeight: '400',
                    color: '#8E8E93',
                    lineHeight: '1.45',
                  }}
                >
                  Your Royale address book
                  <br />
                  Remember everyone you meet
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* The page's onward move — ONE button that never changes state, PINNED
          to the bottom where a nav element would live (user 2026-08-16). It is
          the only action this page has, so it should always be under the thumb
          rather than at the end of a list whose length it doesn't control.

          The reassurance rides directly above it and ARRIVES a beat later,
          once ever per device. Why delayed: the people it is for are the ones
          who hesitate — someone who taps Connect straight away never needed
          it. It leaves with the page (unmounts on navigation), and the
          once-ever flag is written AT REVEAL, so a visit that ended before it
          appeared doesn't burn it. */}
      {showQrPitch && (
        <div
          style={{
            position: 'fixed',
            bottom: 'calc(24px + env(safe-area-inset-bottom))',
            left: 0,
            right: 0,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: '22px',
            padding: '0 20px',
            pointerEvents: 'none',
            zIndex: 20,
          }}
        >
          <PeopleQrPitch variant="line" />
          {/* Above the line in z-order — the reveal rises from behind it */}
          <div style={{ pointerEvents: 'auto', position: 'relative', zIndex: 2 }}>
            <ButtonRounded
              text={
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: '8px' }}>
                  <Lightning size={18} weight="fill" />
                  Connect
                </span>
              }
              onClick={handleGetYourQR}
              variant="black"
              size="auto"
              disabled={minting}
              style={{ minWidth: '160px' }}
            />
          </div>

          {/* The main path (user, 2026-08-20). BELOW the Connect button, not
              above it: the QR is the thing they can do right now with what they
              already have, and the app is the thing they leave with. Reading
              order puts the immediate action first and the durable one last,
              which is also the order of commitment.
              QUIET BY CONSTRUCTION — a grey row, not a button. The 2026-07-17
              freeze happened because a big unprompted CTA read as a demand
              ("what am I supposed to do?"); this states what it is and waits.
              Retires once joined: writeWaitlistJoin stamps the marker AT the
              join and this reads it at render, so the sheet closing (which
              always re-renders this page) is enough — no completion-callback
              wiring, the failure mode that cost three device passes on the
              Profile-side copy. */}
          {!joinedWaitlist && (
            <div
              onClick={() => setShowGetApp(true)}
              style={{
                pointerEvents: 'auto',
                width: '100%',
                maxWidth: '320px',
                display: 'flex',
                alignItems: 'center',
                gap: '12px',
                padding: '12px 14px',
                backgroundColor: '#F2F2F4',
                borderRadius: '16px',
                cursor: 'pointer',
                boxSizing: 'border-box',
                WebkitTapHighlightColor: 'transparent',
              }}
            >
              <div
                style={{
                  width: '40px',
                  height: '40px',
                  borderRadius: '10px',
                  backgroundColor: '#4AA9E9',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0,
                }}
              >
                {/* Source PNG is 96×60 — width-only sizing keeps the ratio
                    (a square box crunched the crown, device pass 3) */}
                <img src={CROWN_WHITE_SMALL} alt="" style={{ width: '24px', height: 'auto' }} />
              </div>
              <div style={{ textAlign: 'left' }}>
                <div style={{ fontSize: '15px', fontWeight: '700', color: '#000000' }}>
                  Get Royale app
                </div>
                {/* "Remember everyone IRL" (user copy 2026-08-20), replacing
                    "Your web app works for 7 days". The old line sold a
                    deadline; this one sells the thing the app is for, and it
                    echoes the Connect button's own framing rather than
                    introducing a second idea at the bottom of the screen. */}
                <div style={{ fontSize: '13px', color: '#8E8E93' }}>Remember everyone IRL</div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Behind the Get-Royale banner. Stage 1 (no intro screen) — the banner
          already made the pitch, so the sheet goes straight to the capture.
          Source 'people_list', distinct from AnonConnectionPage's
          'connection_page', so the two Anon signup doors stay separable in the
          funnel; skipQrGuard for the same Safari-new-context reason. */}
      <LaunchSignupOverlay
        show={showGetApp}
        onClose={() => setShowGetApp(false)}
        onComplete={({ contactType, name }) => {
          setShowGetApp(false);
          // Retire the banner from the completion, not a render-time re-read.
          setJoinedWaitlist(true);
          logFunnelSignupWaitlist(
            sessionChatRoomId,
            'people_list',
            true,
            { contactType, anonName: name },
            { skipQrGuard: true }
          );
        }}
        startAtStage={1}
        prefillPhone={anonPrefillPhone}
        prefillName={anonPrefillName}
        chatRoomId={sessionChatRoomId}
        anonUserId={getAnonUserId()}
        title="Join Royale"
        valueProp=""
        caption="Living address book"
        submitButtonText="Join"
        thanksTitle="You're on the waitlist"
        thanksSubtitle="We'll let you know when the app is ready"
        thanksDwellMs={4000}
      />

      {mintFailed && (
        <NoticeCard
          title="Lost connection for a moment"
          line="Your profile didn't save yet."
          actionLabel="Try again"
          onAction={retryMint}
        />
      )}
    </div>
  );
};

export default AnonConnectionsListPage;
